// src/components/CreateTest.js
import React, { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc as docRef
} from 'firebase/firestore';
import { db } from '../firebase';

export default function CreateTest({
  batchId,
  testId: propTestId = null,
  topic: propTopic = '',
  fullMarks: propFullMarks = 100,
  isEditMode = false,
  onBack = () => {}
}) {
  const [students, setStudents]   = useState([]);
  const [topic, setTopic]         = useState(propTopic);
  const [fullMarks, setFullMarks] = useState(propFullMarks);
  const [marks, setMarks]         = useState({});   // { studentId: number }
  const [resultIds, setResultIds] = useState({});   // { studentId: docId }
  const [message, setMessage]     = useState('');

  // Load students & initialize marks
  useEffect(() => {
    if (!batchId) return;
    (async () => {
      const stuSnap = await getDocs(collection(db,'students'));
      const list = stuSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(s => (s.batch_ids||[]).includes(batchId));
      setStudents(list);

      const m = {}, ids = {};
      if (isEditMode && propTestId) {
        const resSnap = await getDocs(
          query(collection(db,'testResults'), where('test_id','==',propTestId))
        );
        resSnap.docs.forEach(d => {
          const r = d.data();
          m[r.student_id] = r.marks;
          ids[r.student_id] = d.id;
        });
      }
      list.forEach(s => {
        if (m[s.id] === undefined) m[s.id] = 0;
      });
      setMarks(m);
      setResultIds(ids);
    })();
  }, [batchId, isEditMode, propTestId]);

  const handleMarkChange = (sid, val) => {
    setMarks(m => ({ ...m, [sid]: Number(val) }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setMessage('');
    try {
      let testRef;
      if (isEditMode && propTestId) {
        await updateDoc(docRef(db,'tests',propTestId), {
          topic,
          full_marks: fullMarks
        });
        testRef = { id: propTestId };
      } else {
        testRef = await addDoc(collection(db,'tests'), {
          topic,
          full_marks: fullMarks,
          batch_id: batchId,
          created_at: new Date()
        });
      }

      await Promise.all(students.map(s => {
        const rid = resultIds[s.id];
        if (rid) {
          return updateDoc(docRef(db,'testResults',rid), { marks: marks[s.id] });
        } else {
          return addDoc(collection(db,'testResults'), {
            test_id:    testRef.id,
            student_id: s.id,
            marks:      marks[s.id]
          });
        }
      }));

      setMessage(isEditMode ? 'Test updated.' : 'Test and results saved!');
      if (!isEditMode) setTopic('');
    } catch (err) {
      console.error(err);
      setMessage('Error: ' + err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-3xl">
      <h2 className="text-xl font-semibold">
        {isEditMode ? 'Edit Test' : 'Create Test'}
      </h2>

      {/* Topic & Full Marks */}
      <div className="flex gap-4">
        <div>
          <label className="block">Topic</label>
          <input
            type="text"
            value={topic}
            onChange={e => setTopic(e.target.value)}
            required
            disabled={isEditMode}
            className="px-3 py-2 border rounded"
          />
        </div>
        <div>
          <label className="block">Full Marks</label>
          <input
            type="number"
            min={1}
            value={fullMarks}
            onChange={e => setFullMarks(Number(e.target.value))}
            className="px-3 py-2 border rounded w-24"
          />
        </div>
      </div>

      {/* Results Table with Dropdowns */}
      <div className="max-h-64 overflow-auto border rounded">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Student</th>
              <th className="p-2 text-center">Marks</th>
            </tr>
          </thead>
          <tbody>
            {students.map(s => (
              <tr key={s.id} className="odd:bg-white even:bg-gray-50">
                <td className="p-2">{s.name}</td>
                <td className="p-2 text-center">
                  <select
                    value={marks[s.id]}
                    onChange={e => handleMarkChange(s.id, e.target.value)}
                    className="px-1 py-1 border rounded"
                  >
                    {Array.from({ length: fullMarks + 1 }, (_, i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Submit & (only in create mode) Back */}
      <div className="flex items-center gap-4">
        <button
          type="submit"
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          {isEditMode ? 'Save Changes' : 'Submit Test'}
        </button>
        {!isEditMode && (
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
          >
            Back to Batches
          </button>
        )}
      </div>

      {message && <p className="text-sm mt-2">{message}</p>}
    </form>
  );
}
